import type {DiagramView} from '../CarDiagram.types';

export interface PartMapping {
  readonly view: DiagramView;
  readonly parts: readonly string[];
}

/**
 * Maps API prediction category IDs to the diagram view and part IDs they affect.
 * Priority for color conflicts: damage > service > suggestions.
 *
 * Each `parts` entry MUST be a part-id that the target view registers with a
 * `variant` prop (i.e. is interactive). Listing a part-id that is only painted
 * as a static background in that view is a silent bug — the highlight will
 * never render. When adding new categories, cross-check the corresponding
 * `views/<View>View.tsx` for the exact `partVariants[...]` keys.
 *
 * Categories not present in this map are silently dropped by `useCarDiagramData`
 * (they still appear in the list view). Categories intentionally omitted —
 * because no SVG primitive faithfully represents them — are listed below:
 *   - `SAFETY_AND_ASSISTANCE_SYSTEMS` — sensors/airbags/ABS scattered across
 *     the body, no single diagram anchor.
 *   - `IGNITION` — spark plugs / coils not modelled as a separate SVG.
 *   - `ENGINE_ACCESSORIES` — belts, alternator pulleys, idlers not modelled.
 *   - `LIGHTING_INTERIOR` — cabin lights not modelled.
 *
 * Keys are sorted alphabetically by category id.
 */
export const CATEGORY_TO_PARTS: Record<string, PartMapping> = {
  AIR_CONDITIONING: {
    view: 'interior',
    // Anchor on the central HVAC controls and the rear vents. Seats are not an
    // AC failure surface and were dropped from the previous over-broad mapping.
    parts: [
      'dashboard-middle-heating',
      'rear-left-side-panel-interior',
      'rear-right-side-panel-interior',
    ],
  },
  BODY: {
    view: 'exterior',
    parts: [
      'hood',
      'trunk',
      'front-bumper',
      'rear-bumper',
      'front-left-side-panel',
      'front-right-side-panel',
      'rear-left-side-panel',
      'rear-right-side-panel',
      'roof',
      'tow-bar',
      'grille',
    ],
  },
  BRAKE_SYSTEM: {
    view: 'mechanics',
    // `brake-fluid` is registered only in EngineView, so it cannot be
    // highlighted from the mechanics view — keep the four discs only.
    parts: ['front-left-brake', 'front-right-brake', 'rear-left-brake', 'rear-right-brake'],
  },
  CLUTCH: {
    view: 'engine',
    // Closest engine-bay anchor for the clutch hydraulic loop. The clutch
    // disc/pressure plate themselves are not separately rendered.
    parts: ['clutch-fluid'],
  },
  DIFFERENTIAL: {
    view: 'mechanics',
    parts: ['rear-differencial', 'torsen-differencial'],
  },
  DOORS_WINDOWS: {
    view: 'exterior',
    parts: [
      'front-left-door',
      'front-right-door',
      'rear-left-door',
      'rear-right-door',
      'front-windshield',
      'rear-windshield',
    ],
  },
  ELECTRICAL: {
    view: 'electric',
    // The 12V `battery` SVG lives in EngineView, not ElectricView, and is
    // owned by `ENGINE_ELECTRICAL` (Czech `elektrické součásti motoru
    // (baterie)`). `ELECTRICAL` covers high-voltage / wiring / ECU only.
    parts: [
      'traction-battery',
      'traction-battery-cables',
      'front-electric-engine',
      'rear-electric-engine',
    ],
  },
  ENGINE_COOLING: {
    view: 'engine',
    parts: ['air-cooling-system', 'engine-coolant', 'condensator'],
  },
  ENGINE_ELECTRICAL: {
    view: 'engine',
    // Backend `ENGINE_ELECTRICAL` Czech text explicitly bundles the 12V
    // battery (`elektrické součásti motoru (baterie)`). Starter motor and
    // alternator have no separate SVG primitives, so the battery icon is
    // the closest anchor in the engine view.
    parts: ['battery'],
  },
  ENGINE_INTAKE: {
    view: 'engine',
    parts: ['filters', 'air-cooling-system'],
  },
  ENGINE_LUBRICATION: {
    view: 'engine',
    // The previous mapping pointed at `power-steering-fluid`,
    // `windscreen-wash-fluid` and `clutch-fluid` — none of which are engine
    // oil. The actual engine oil reservoir SVG (registered as
    // `engine-oil-reservoir` in EngineView) plus the oil pipework is the
    // correct anchor.
    parts: ['engine-oil-reservoir', 'pipes'],
  },
  ENGINE_MECHANICAL: {
    view: 'engine',
    // Highlight only the engine block until the additional mechanical part SVG ids
    // are confirmed. Adding placeholders here would silently miss highlights when
    // the diagram tries to look the ids up.
    parts: ['engine-block'],
  },
  EXHAUST_SYSTEM: {
    view: 'mechanics',
    parts: ['exhaust-system'],
  },
  FUEL_SYSTEM: {
    view: 'mechanics',
    parts: ['gas-tank', 'exhaust-system'],
  },
  HEATING: {
    view: 'interior',
    parts: ['dashboard-middle-heating'],
  },
  INSTRUMENTS: {
    view: 'interior',
    // `dashboard-middle-heating` is the HVAC anchor for HEATING/AC and would
    // collide here. The center console is the closest interior surface for
    // the instrument cluster + diagnostic activity that dominates this
    // category's training data (Czech `přístroje`).
    parts: ['center-console'],
  },
  LIGHTING_EXTERIOR: {
    view: 'exterior',
    parts: [
      'front-left-headlight',
      'front-right-headlight',
      'rear-left-taillight',
      'rear-right-taillight',
    ],
  },
  LINKAGE_SYSTEM: {
    view: 'mechanics',
    // Czech `pákové ústrojí` training data is dominated by half-shaft CV
    // boots (`vymena manziet poloos`), drive-shaft and shifter linkage.
    // CV boot SVGs live on the sideshafts.
    parts: [
      'front-left-sideshaft',
      'front-right-sideshaft',
      'rear-left-sideshaft',
      'rear-right-sideshaft',
      'drive-shaft',
    ],
  },
  STEERING: {
    view: 'interior',
    parts: ['steering-wheel'],
  },
  TIMING_MECHANISM: {
    view: 'engine',
    // Timing belt/chain has no dedicated SVG. The engine block houses it,
    // so it is the most accurate anchor we have today.
    parts: ['engine-block'],
  },
  TRANSMISSION_AUTOMATIC: {
    view: 'mechanics',
    parts: ['transmission'],
  },
  TRANSMISSION_MANUAL: {
    view: 'mechanics',
    parts: ['transmission'],
  },
  WHEEL_SUSPENSION: {
    view: 'mechanics',
    // `left-upper-suspension-cover` / `right-upper-suspension-cover` are only
    // registered in `EngineView` (engine-bay strut tops), not in
    // `MechanicsView`, so they cannot be highlighted from this view and are
    // intentionally omitted here.
    parts: [
      'front-left-upper-control-arm',
      'front-right-upper-control-arm',
      'rear-left-upper-control-arm',
      'rear-right-upper-control-arm',
      'front-left-shock-absorber',
      'front-right-shock-absorber',
      'rear-left-shock-absorber',
      'rear-right-shock-absorber',
      'torsion-bar',
      'anti-roll-bar',
      'steering-bar',
    ],
  },
  WHEELS_TIRES: {
    view: 'mechanics',
    parts: ['front-left-wheel', 'front-right-wheel', 'rear-left-wheel', 'rear-right-wheel'],
  },
  WIPERS_WASHERS: {
    view: 'engine',
    // Washer fluid reservoir lives in the engine bay — the wiper blades
    // themselves are not rendered as a separate SVG primitive.
    parts: ['windscreen-wash-fluid'],
  },
};

export const VARIANT_PRIORITY = {
  damage: 3,
  service: 2,
  suggestions: 1,
  default: 0,
} as const;

/**
 * Reverse lookup: partId → categoryId
 * Derived from CATEGORY_TO_PARTS for O(1) access.
 */
function buildPartToCategoryMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [categoryId, mapping] of Object.entries(CATEGORY_TO_PARTS)) {
    for (const partId of mapping.parts) {
      // A part may intentionally appear in multiple categories (e.g., air-cooling-system
      // belongs to both ENGINE_COOLING and ENGINE_INTAKE). The last writer wins for the
      // reverse lookup — this is expected and by design.
      map[partId] = categoryId;
    }
  }
  return map;
}

export const PART_TO_CATEGORY: Record<string, string> = buildPartToCategoryMap();
